import {
  FileText,
  MessagesSquare,
  Pencil,
  Plus,
  RotateCcw,
  Send,
  Trash2,
  Undo2,
} from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { CheckboxField, Field, FormError, TextareaField } from '@/components/ui/field';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  INVOICE_TONES,
  useAdminInvoice,
  useRefundPayment,
  useReissueInvoice,
  useSendInvoice,
  useUpdateInvoice,
  useUpdateLineItem,
  useRemoveLineItem,
} from '@/hooks/invoices';
import { ApiError } from '@/lib/api';
import { formatDay } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import {
  AddJobsDialog,
  AddLineDialog,
  RecordPaymentDialog,
  VoidInvoiceDialog,
} from '@/pages/admin/invoices/InvoiceDialogs';
import { centsToInput, formatCents, parseMoneyInput } from '@shared/money';
import type {
  AdminInvoiceDetail,
  AdminPaymentView,
  InvoiceLineItemView,
} from '@shared/schemas/invoice';

/*
 * /admin/invoices/:id — the builder, and the record — ROADMAP.md §12.2–12.5.
 *
 * **Auto-calculation is the point of this screen** (§12.2): every edit answers
 * with the server's own recomputed figures, and nothing here adds anything up
 * — the browser draws totals, it never produces them.
 *
 * The screen changes shape with the status rather than disabling itself
 * piecemeal: a draft is a workbench, a sent invoice is a record with a
 * payments ledger, a void one is a gravestone naming its replacement.
 */

/** One line of a draft, edited in place through its own dialog. */
function EditLineDialog({
  invoiceId,
  line,
  onClose,
}: {
  invoiceId: string;
  line: InvoiceLineItemView | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const update = useUpdateLineItem(invoiceId);

  const [description, setDescription] = useState(line?.description ?? '');
  const [quantity, setQuantity] = useState(line === null ? '1' : String(line.quantity));
  const [price, setPrice] = useState(line === null ? '' : centsToInput(line.unitPriceCents));
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);

  // Remount per line via key= on the caller, so state follows the row.
  if (line === null) return null;

  const apiError = update.error instanceof ApiError ? update.error : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (line === null) return;

    const parsedPrice = parseMoneyInput(price);
    if (!parsedPrice.ok) {
      setFieldError(parsedPrice.message);
      return;
    }
    if (parsedPrice.cents === null) {
      setFieldError('Enter a price — 0 is fine for “no charge”');
      return;
    }
    const parsedQuantity = Number(quantity);
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      setFieldError('Quantity must be more than zero');
      return;
    }
    if (description.trim() === '') {
      setFieldError('Every line needs a description');
      return;
    }
    setFieldError(undefined);

    update.mutate(
      {
        lineId: line.id,
        description: description.trim(),
        quantity: parsedQuantity,
        unitPriceCents: parsedPrice.cents,
      },
      {
        onSuccess: () => {
          toast.success('Line updated.');
          onClose();
        },
      },
    );
  }

  return (
    <ConfirmShell title="Edit line" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field
          label="Description"
          value={description}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
          required
        />
        <div className="grid grid-cols-2 gap-3">
          <Field
            label={line.pricingUnit === 'hourly' ? 'Hours' : 'Quantity'}
            type="number"
            // The pair travels together — see AddLineDialog's note.
            step={line.pricingUnit === 'hourly' ? '0.25' : '1'}
            min={line.pricingUnit === 'hourly' ? '0.25' : '1'}
            value={quantity}
            onChange={(event) => {
              setQuantity(event.target.value);
            }}
          />
          <Field
            label={line.pricingUnit === 'hourly' ? 'Rate per hour' : 'Unit price'}
            inputMode="decimal"
            value={price}
            onChange={(event) => {
              setPrice(event.target.value);
            }}
          />
        </div>

        {fieldError !== undefined ? <FormError>{fieldError}</FormError> : null}
        {apiError ? <FormError>{apiError.message}</FormError> : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save line'}
          </Button>
        </div>
      </form>
    </ConfirmShell>
  );
}

/** The small always-open modal shell the edit dialog uses. */
function ConfirmShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-ink-900/40"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative max-h-[calc(100vh-2rem)] w-[min(28rem,calc(100vw-2rem))] overflow-y-auto rounded-lg bg-white p-6 shadow-card"
      >
        <h2 className="text-h2 text-ink-900">{title}</h2>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}

/** Notes editor for drafts: saved on blur, like nothing was ever a form. */
function DraftNotes({ detail }: { detail: AdminInvoiceDetail }) {
  const toast = useToast();
  const update = useUpdateInvoice(detail.id);
  const [notes, setNotes] = useState(detail.notes ?? '');
  const [internalNotes, setInternalNotes] = useState(detail.internalNotes ?? '');

  function saveIfChanged() {
    const nextNotes = notes.trim() === '' ? null : notes.trim();
    const nextInternal = internalNotes.trim() === '' ? null : internalNotes.trim();
    if (nextNotes === detail.notes && nextInternal === detail.internalNotes) return;

    update.mutate(
      { notes: nextNotes, internalNotes: nextInternal },
      {
        onSuccess: () => {
          toast.success('Notes saved.');
        },
      },
    );
  }

  return (
    <div className="space-y-4">
      <TextareaField
        label="Notes to the client"
        value={notes}
        onChange={(event) => {
          setNotes(event.target.value);
        }}
        onBlur={saveIfChanged}
        rows={2}
        hint="Goes on the PDF and in their portal."
      />
      <TextareaField
        label="Internal notes"
        value={internalNotes}
        onChange={(event) => {
          setInternalNotes(event.target.value);
        }}
        onBlur={saveIfChanged}
        rows={2}
        hint="Never sent and never shown to the client — the office’s margin."
      />
    </div>
  );
}

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const invoice = useAdminInvoice(id);
  const send = useSendInvoice(id ?? '');
  const reissue = useReissueInvoice(id ?? '');
  const refund = useRefundPayment(id ?? '');
  const update = useUpdateInvoice(id ?? '');
  const removeLine = useRemoveLineItem(id ?? '');

  const [confirmingSend, setConfirmingSend] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [addingJobs, setAddingJobs] = useState(false);
  const [addingLine, setAddingLine] = useState(false);
  const [payingOpen, setPayingOpen] = useState(false);
  const [editingLine, setEditingLine] = useState<InvoiceLineItemView | null>(null);
  const [refunding, setRefunding] = useState<AdminPaymentView | null>(null);
  /*
   * The tax checkbox answers the click immediately and reconciles when the
   * server does. Bound straight to `detail.taxEnabled` it stayed still until
   * the round trip finished — a dead-feeling control that invites a second
   * click and a double toggle, and the e2e run caught it as "clicking did not
   * change its state".
   */
  const [taxIntent, setTaxIntent] = useState<boolean | null>(null);

  if (invoice.isPending) {
    return (
      <AdminPage title="Invoices" record="Loading…">
        <div className="bg-cream-100 h-64 animate-pulse rounded-lg" />
      </AdminPage>
    );
  }

  if (invoice.error || invoice.data === undefined) {
    return (
      <AdminPage title="Invoices" record="Invoice">
        <ErrorState
          title="This invoice could not be loaded."
          onRetry={() => {
            void invoice.refetch();
          }}
        />
      </AdminPage>
    );
  }

  const detail = invoice.data;
  const isDraft = detail.status === 'draft';
  const isVoid = detail.status === 'void';

  function doSend() {
    send.mutate(undefined, {
      onSuccess: (result) => {
        setConfirmingSend(false);
        if (result.delivery.status === 'sent') toast.success(result.delivery.message);
        else toast.error(result.delivery.message);
      },
      onError: (error) => {
        setConfirmingSend(false);
        toast.error(error instanceof ApiError ? error.message : 'The send failed. Try again.');
      },
    });
  }

  return (
    <AdminPage
      title="Invoices"
      record={detail.invoiceNumber}
      actions={
        <div className="flex flex-wrap gap-2">
          {isDraft ? (
            <>
              <Button
                onClick={() => {
                  setConfirmingSend(true);
                }}
              >
                <Send aria-hidden="true" className="size-4" />
                Send to client
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setVoiding(true);
                }}
              >
                Void
              </Button>
            </>
          ) : null}
          {detail.status === 'sent' || detail.status === 'paid' ? (
            <>
              {detail.status === 'sent' ? (
                <Button
                  onClick={() => {
                    setPayingOpen(true);
                  }}
                >
                  Record a payment
                </Button>
              ) : null}
              <Button
                variant="outline"
                disabled={send.isPending}
                onClick={() => {
                  setConfirmingSend(true);
                }}
              >
                <Send aria-hidden="true" className="size-4" />
                Send again
              </Button>
              {detail.status === 'sent' ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setVoiding(true);
                  }}
                >
                  Void
                </Button>
              ) : null}
            </>
          ) : null}
          {isVoid && detail.replacedByInvoiceId === null ? (
            <Button
              disabled={reissue.isPending}
              onClick={() => {
                reissue.mutate(undefined, {
                  onSuccess: (fresh) => {
                    toast.success(`${fresh.invoiceNumber} drafted to replace this one.`);
                    void navigate(`/admin/invoices/${fresh.id}`);
                  },
                });
              }}
            >
              <RotateCcw aria-hidden="true" className="size-4" />
              Reissue
            </Button>
          ) : null}
        </div>
      }
    >
      {/* ------------------------------------------------ header facts --- */}
      <div className="mb-6 flex flex-wrap items-center gap-x-6 gap-y-2">
        <StatusBadge label={detail.statusLabel} tone={INVOICE_TONES[detail.status]} />
        {detail.isOverdue ? (
          <span className="rounded-sm bg-red-100 px-2 py-0.5 text-meta font-medium text-red-800">
            Overdue
          </span>
        ) : null}
        <span className="text-body text-ink-700">
          For{' '}
          <Link
            to={`/admin/clients/${detail.clientId}`}
            className="text-bark-700 underline underline-offset-2"
          >
            {detail.clientName}
          </Link>
        </span>
        {detail.issueDate !== null ? (
          <span className="text-body text-ink-500">Issued {formatDay(detail.issueDate)}</span>
        ) : null}
        {detail.dueDate !== null ? (
          <span className="text-body text-ink-500">
            Due {detail.dueDate === detail.issueDate ? 'on receipt' : formatDay(detail.dueDate)}
          </span>
        ) : null}
        {detail.hasPdf ? (
          <a
            href={`/api/admin/invoices/${detail.id}/pdf`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-touch items-center gap-1 text-body text-bark-700 underline underline-offset-2"
          >
            <FileText aria-hidden="true" className="size-4" />
            PDF
          </a>
        ) : null}
        {detail.conversationId !== null ? (
          <Link
            to={`/admin/conversations/${detail.conversationId}`}
            className="inline-flex min-h-touch items-center gap-1 text-body text-bark-700 underline underline-offset-2"
          >
            <MessagesSquare aria-hidden="true" className="size-4" />
            Conversation
          </Link>
        ) : null}
      </div>

      {/* ------------------------------------------------- void banner --- */}
      {isVoid ? (
        <div className="bg-cream-100 mb-6 rounded-lg border border-ink-300 p-4">
          <p className="text-body text-ink-900">
            Voided {detail.voidedAt === null ? '' : formatDay(detail.voidedAt.slice(0, 10))} —{' '}
            {detail.voidReason}
          </p>
          <p className="mt-1 text-body text-ink-700">
            {detail.replacedByInvoiceId !== null ? (
              <>
                Replaced by{' '}
                <Link
                  to={`/admin/invoices/${detail.replacedByInvoiceId}`}
                  className="text-bark-700 underline underline-offset-2"
                >
                  {detail.replacedByInvoiceNumber}
                </Link>
                .
              </>
            ) : (
              'Its number stays burnt; its jobs can be billed again.'
            )}
          </p>
        </div>
      ) : null}

      {detail.replacesInvoiceId !== null ? (
        <p className="mb-6 text-body text-ink-500">
          <Undo2 aria-hidden="true" className="mr-1 inline size-4" />
          Replaces{' '}
          <Link
            to={`/admin/invoices/${detail.replacesInvoiceId}`}
            className="text-bark-700 underline underline-offset-2"
          >
            {detail.replacesInvoiceNumber}
          </Link>
          , which was voided.
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* ------------------------------------------------- the lines --- */}
        <section className="lg:col-span-2" aria-label="Line items">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-h2 text-ink-900">Lines</h2>
            {isDraft ? (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setAddingJobs(true);
                  }}
                >
                  <Plus aria-hidden="true" className="size-4" />
                  Add jobs
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setAddingLine(true);
                  }}
                >
                  <Plus aria-hidden="true" className="size-4" />
                  Add a line
                </Button>
              </div>
            ) : null}
          </div>

          {detail.lineItems.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No lines yet."
              hint="Add the completed jobs to bill, or a one-off charge. Sending needs at least one line."
            />
          ) : (
            <ul className="divide-y divide-ink-300 rounded-lg border border-ink-300 bg-white">
              {detail.lineItems.map((line) => (
                <li key={line.id} className="flex items-start gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-body text-ink-900">{line.description}</p>
                    <p className="mt-0.5 text-meta text-ink-500">
                      {line.jobNumber !== null ? `${line.jobNumber} · ` : ''}
                      {line.pricingUnit === 'hourly'
                        ? `${String(line.quantity)} hr × ${formatCents(line.unitPriceCents)}/hr`
                        : line.quantity !== 1
                          ? `${String(line.quantity)} × ${formatCents(line.unitPriceCents)}`
                          : formatCents(line.unitPriceCents)}
                    </p>
                  </div>
                  <span className="whitespace-nowrap text-body font-medium tabular-nums text-ink-900">
                    {formatCents(line.totalCents)}
                  </span>
                  {isDraft ? (
                    <div className="flex shrink-0 gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Edit line: ${line.description}`}
                        onClick={() => {
                          setEditingLine(line);
                        }}
                      >
                        <Pencil aria-hidden="true" className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Remove line: ${line.description}`}
                        disabled={removeLine.isPending}
                        onClick={() => {
                          removeLine.mutate(line.id, {
                            onSuccess: () => {
                              toast.success('Line removed.');
                            },
                          });
                        }}
                      >
                        <Trash2 aria-hidden="true" className="size-4" />
                      </Button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}

          {/* --------------------------------------------- payments ---- */}
          {detail.status !== 'draft' && !isVoid ? (
            <div className="mt-8">
              <h2 className="mb-3 text-h2 text-ink-900">Payments</h2>
              {detail.payments.length === 0 ? (
                <p className="text-body text-ink-500">
                  Nothing recorded yet. Money is entered by hand when it arrives — cash, check, bank
                  transfer, or card in person.
                </p>
              ) : (
                <ul className="divide-y divide-ink-300 rounded-lg border border-ink-300 bg-white">
                  {detail.payments.map((row) => (
                    <li key={row.id} className="flex items-start gap-3 p-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-body text-ink-900">
                          {formatCents(row.amountCents)} · {row.methodLabel}
                          {row.isRefunded ? (
                            <span className="bg-cream-100 ml-2 rounded-sm px-1.5 py-0.5 text-meta text-ink-700">
                              Refunded
                            </span>
                          ) : null}
                        </p>
                        <p className="mt-0.5 text-meta text-ink-500">
                          Arrived {formatDay(row.paidOn)}
                          {row.reference !== null ? ` · Ref ${row.reference}` : ''} · entered by{' '}
                          {row.recordedByName}
                        </p>
                        {row.notes !== null ? (
                          <p className="mt-0.5 text-meta text-ink-500">{row.notes}</p>
                        ) : null}
                      </div>
                      {!row.isRefunded ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setRefunding(row);
                          }}
                        >
                          Refund
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </section>

        {/* --------------------------------------------------- totals --- */}
        <aside aria-label="Totals">
          <div className="rounded-lg border border-ink-300 bg-white p-4">
            <dl className="space-y-2 text-body">
              <div className="flex justify-between">
                <dt className="text-ink-500">Subtotal</dt>
                <dd className="tabular-nums text-ink-900">{formatCents(detail.subtotalCents)}</dd>
              </div>
              {isDraft ? (
                <div>
                  <CheckboxField
                    label="Apply Utah sales tax"
                    name="taxEnabled"
                    checked={taxIntent ?? detail.taxEnabled}
                    onCheckedChange={(checked) => {
                      setTaxIntent(checked);
                      update.mutate(
                        { taxEnabled: checked },
                        {
                          onSettled: () => {
                            setTaxIntent(null);
                          },
                        },
                      );
                    }}
                  />
                  <p className="text-meta text-ink-500">
                    {detail.taxEnabled
                      ? `${(detail.taxRateBps / 100).toFixed(2)}% against the subtotal — frozen when sent.`
                      : 'Off by default — whether Utah taxes cleaning is the accountant’s call.'}
                  </p>
                </div>
              ) : null}
              {detail.taxEnabled ? (
                <div className="flex justify-between">
                  <dt className="text-ink-500">
                    Utah sales tax ({(detail.taxRateBps / 100).toFixed(2)}%)
                  </dt>
                  <dd className="tabular-nums text-ink-900">{formatCents(detail.taxCents)}</dd>
                </div>
              ) : null}
              <div className="flex justify-between border-t border-ink-300 pt-2 text-label font-semibold">
                <dt className="text-ink-900">Total</dt>
                <dd className="tabular-nums text-ink-900">{formatCents(detail.totalCents)}</dd>
              </div>
              {/* Zero is $0.00 — an unpaid invoice has been paid exactly nothing. */}
              <div className="flex justify-between">
                <dt className="text-ink-500">Paid</dt>
                <dd className="tabular-nums text-ink-900">{formatCents(detail.amountPaidCents)}</dd>
              </div>
              <div className="flex justify-between text-label font-semibold">
                <dt className="text-ink-900">Balance</dt>
                <dd className="tabular-nums text-ink-900">{formatCents(detail.balanceCents)}</dd>
              </div>
            </dl>
          </div>

          <div className="mt-6">
            {isDraft ? (
              <DraftNotes key={detail.id} detail={detail} />
            ) : (
              <div className="space-y-4">
                {detail.notes !== null ? (
                  <div>
                    <h3 className="text-label font-medium text-ink-900">Notes to the client</h3>
                    <p className="mt-1 whitespace-pre-wrap text-body text-ink-700">
                      {detail.notes}
                    </p>
                  </div>
                ) : null}
                {detail.internalNotes !== null ? (
                  <div>
                    <h3 className="text-label font-medium text-ink-900">
                      Internal notes <span className="font-normal text-ink-500">— never sent</span>
                    </h3>
                    <p className="mt-1 whitespace-pre-wrap text-body text-ink-700">
                      {detail.internalNotes}
                    </p>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* ------------------------------------------------------ dialogs --- */}
      <ConfirmDialog
        open={confirmingSend}
        onOpenChange={setConfirmingSend}
        title={isDraft ? `Send ${detail.invoiceNumber}?` : `Send another copy?`}
        description={
          isDraft ? (
            <>
              This issues the invoice — dates set, tax rate frozen, number final — generates the
              PDF, and emails it to {detail.clientEmail ?? 'the client'} through their conversation
              thread. After this, changes mean voiding and reissuing.
            </>
          ) : (
            <>
              Another copy of the PDF goes to {detail.clientEmail ?? 'the client'} on the same
              conversation thread. Nothing about the invoice changes.
            </>
          )
        }
        confirmLabel={isDraft ? 'Issue and send' : 'Send another copy'}
        isPending={send.isPending}
        onConfirm={doSend}
      />

      <ConfirmDialog
        open={refunding !== null}
        onOpenChange={(next) => {
          if (!next) setRefunding(null);
        }}
        title={refunding === null ? '' : `Refund ${formatCents(refunding.amountCents)}?`}
        description="The payment stays on the record marked refunded — money records are never deleted. If this un-meets the balance, the invoice goes back to Sent."
        confirmLabel="Mark it refunded"
        destructive
        isPending={refund.isPending}
        onConfirm={() => {
          if (refunding === null) return;
          refund.mutate(refunding.id, {
            onSuccess: () => {
              setRefunding(null);
              toast.success('Payment marked refunded.');
            },
          });
        }}
      />

      {editingLine !== null ? (
        <EditLineDialog
          key={editingLine.id}
          invoiceId={detail.id}
          line={editingLine}
          onClose={() => {
            setEditingLine(null);
          }}
        />
      ) : null}

      <AddJobsDialog invoice={detail} open={addingJobs} onOpenChange={setAddingJobs} />
      <AddLineDialog invoice={detail} open={addingLine} onOpenChange={setAddingLine} />
      <RecordPaymentDialog
        key={`pay-${String(detail.amountPaidCents)}`}
        invoice={detail}
        open={payingOpen}
        onOpenChange={setPayingOpen}
      />
      <VoidInvoiceDialog invoice={detail} open={voiding} onOpenChange={setVoiding} />
    </AdminPage>
  );
}
